import _ from 'lodash'
import { Query } from 'shared-libs/models/query'
import type webApis from 'browser/app/models/apis'
import type { Place } from '../interface'
import type { Geolocation } from 'shared-libs/generated/server-types/entity/address'

type CityLocation = Pick<Place, 'locality' | 'region'>
type CityOption = CityLocation & { placeId?: string; geolocation?: Geolocation }
type CityLoadCount = { data: string; computations: { countEntities: number } }
type PlacePrediction = { terms: Array<{ value: string }>; place_id: string }
type GeocodedPlace = { geometry: { location: { lat(): number; lng(): number } } }

export class CityQuery {
  private apis: any
  private entityType: string
  private filterKey: string

  constructor(apis: typeof webApis, entityType: string, filters: object[], filterKey: string) {
    this.apis = apis
    this.entityType = entityType
    this.filterKey = filterKey
  }

  public getGeolocationForOption(option: CityOption) {
    if (option.geolocation) {
      return Promise.resolve(option.geolocation)
    }
    return this.apis.getGoogleMapsPlace(option.placeId).then((place: GeocodedPlace[] | null) => {
      return {
        latitude: place![0].geometry.location.lat(),
        longitude: place![0].geometry.location.lng(),
      }
    })
  }

  public queryLocationOptions(query: string) {
    const autoCompleteOptions = { types: ['(cities)'] }
    return this.apis.getGoogleMapsPlacesAutomComplete(query, autoCompleteOptions)
      .then((locations: PlacePrediction[] | null) => {
        const options = _.map(locations, (location, index) => ({
          displayName: `${location.terms[0].value}, ${location.terms[1].value}`,
          locality: location.terms[0].value,
          numLoads: 0,
          placeId: location.place_id,
          region: location.terms[1].value,
        }))
        return this.queryLoadCountForLocations(options).then((loadsByLocation: CityLoadCount[]) => {
          _.forEach(options, (option) => {
            option.numLoads = this.getLoadCountForLocation(option, loadsByLocation)
          })
          return options
        })
      })
  }

  public queryLoadCountForGeolocation(geolocation: Geolocation, radius: string | number) {
    const request = {
      computations: {
        countEntities: {
          path: 'uniqueId',
          type: 'valueCount',
        },
      },
      filters: [
        {
          center: geolocation,
          distance: radius,
          distanceUnit: 'miles',
          key: this.filterKey,
          path: `${this.filterKey}.geolocation`,
          type: 'geoDistance',
        },
      ],
      groups: [
        {
          lanePointBucketType: 'localityRegion',
          path: this.filterKey,
          type: 'lanePoint',
        },
      ],
    }
    return new Query(this.apis)
      .setComputations(request.computations)
      .setEntityType(this.entityType)
      .setFilters(request.filters)
      .getCollection().find()
  }

  public queryLoadCountForLocation(location: CityLocation) {
    return this.queryLoadCountForLocations([location])
  }

  private getLoadCountForLocation(location: CityLocation, loadCountsByLocation: CityLoadCount[]) {
    const { locality, region } = location
    for (const loadCount of loadCountsByLocation) {
      if (loadCount.data === `${locality}--${region}`) {
        return loadCount.computations.countEntities
      }
    }
    return 0
  }

  private queryLoadCountForLocations(locations: CityLocation[]) {
    const localities = _.map(locations, 'locality')
    const region = _.uniq(_.map(locations, 'region'))
    const request = {
      computations: {
        countEntities: {
          path: 'uniqueId',
          type: 'valueCount',
        },
      },
      filters: [
        {
          path: `${this.filterKey}.locality`,
          type: 'match',
          values: localities,
        },
        {
          path: `${this.filterKey}.region`,
          type: 'match',
          values: region,
        },
      ],
      groups: [
        {
          path: `${this.filterKey}.localityRegion`,
          type: 'field',
        },
      ],
    }
    return new Query(this.apis)
      .setComputations(request.computations)
      .setEntityType(this.entityType)
      .setFilters(request.filters)
      .setGroups(request.groups)
      .getCollection().find()
  }
}
